import React, { useState, useContext, useEffect, useCallback } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  Button, Typography, TableSortLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import * as Constants from '../Constants';
import { AuthContext } from '../AuthContextProvider';
import ErrorDialog from '../ErrorDialog';
import { DateTime } from 'luxon';
import { rpc } from '../utils/RpcUtils';
import { getTZDateTimeAsString, isValidDateTime } from '../utils/DateUtils';
import '../App.css';
import PropTypes from 'prop-types';

const Usage = ({ allSites = false }) => {
  const { user } = useContext(AuthContext);
  const userTimeZone = user?._site?.timezone || 'system';
  const [startDate, setStartDate] = useState(DateTime.now().setZone(userTimeZone).minus({ days: 1 }));
  const [endDate, setEndDate] = useState(DateTime.now().setZone(userTimeZone));
  const [errorMessage, setErrorMessage] = useState('');
  const [usages, setUsages] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);;

  const searchUsages = useCallback(async () => {
    if (!isValidDateTime(startDate) || !isValidDateTime(endDate)) {
      setErrorMessage('Start Date and End Date are required');
      setOpenErrorDialog(true);
      return;
    }

    const params = {
      when: { $gte: startDate.toISO(), $lte: endDate.toISO() },
    };

    if (!allSites && user?._site?._id) {
      params._site = user._site._id;
    }

    await rpc({
      method: 'GetUsages',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess: setUsages,
    });
  }, [startDate, endDate, allSites, user?._site?._id]);

  useEffect(() => {
    searchUsages();
  }, [searchUsages]);

  const handleStartDateChange = (date) => setStartDate(date);
  const handleEndDateChange = (date) => setEndDate(date);
  const handleClose = () => setOpenErrorDialog(false);

  const getUserName = (usage) => {
    if (usage._user?.email) {
      return `${usage._user?.first || ''} ${usage._user?.last || ''}`.trim() + 
        ` (${usage._user?.email || ''})`;
    } else if (usage.who) {
      return usage.who;
    }else {
      return '';
    }
  };

  const getEventInfo = (usage) => {
    if (usage._event?.displayName) {
      return usage._event?.displayName;
    } else if (usage.extra) {
      return usage.extra;
    }else {
      return '';
    }
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(column);

    const sortedUsages = [...usages].sort((a, b) => {
      let valueA = '';
      let valueB = '';

      if (column === '_site') {
        valueA = a._site?.name ?? '';
        valueB = b._site?.name ?? '';
      } else if (column === 'who') {
        valueA = getUserName(a);
        valueB = getUserName(b);
      } else if (column === 'extra') {
        valueA = getEventInfo(a);
        valueB = getEventInfo(b);
      } else {
        valueA = a[column] ?? '';
        valueB = b[column] ?? '';
      }

      if (column === 'when') {
        const dtA = DateTime.fromISO(valueA);
        const dtB = DateTime.fromISO(valueB);
        const timeA = dtA.isValid ? dtA.toMillis() : 0;
        const timeB = dtB.isValid ? dtB.toMillis() : 0;
        return newOrder === 'asc' ? timeA - timeB : timeB - timeA;
      } else {
        return newOrder === 'asc' 
          ? valueA.toString().localeCompare(valueB.toString()) 
          : valueB.toString().localeCompare(valueA.toString());
      }
    });

    setUsages(sortedUsages);
  };

  return (
    <div className="Usage">
      <Typography variant="h6" gutterBottom>
        Usage Report
      </Typography>
      
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en">
        <DateTimePicker className="DatePicker"
          label="Start Date"
          value={startDate}
          onChange={handleStartDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          format={Constants.DATE_TIME_FORMAT_LUXON}
        />
        <DateTimePicker className="DatePicker"
          label="End Date"
          value={endDate}
          onChange={handleEndDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          format={Constants.DATE_TIME_FORMAT_LUXON}
        />
      </LocalizationProvider>

      <Button className="Button" variant="contained" color="primary" onClick={searchUsages}>
          Search
      </Button>

      <TableContainer className="TableContainer" component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              {/* 2. Conditionally render Site column header with sorting capability */}
              {allSites && (
                <TableCell>
                  <TableSortLabel active={orderBy === '_site'} direction={order} onClick={() => handleSort('_site')}>
                    <b>Site</b>
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell>
                <TableSortLabel active={orderBy === 'who'} direction={order} onClick={() => handleSort('who')}>
                  <b>Who</b>
                </TableSortLabel>
              </TableCell>
              <TableCell><b>What</b></TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'when'} direction={order} onClick={() => handleSort('when')}>
                  <b>When</b>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'extra'} direction={order} onClick={() => handleSort('extra')}>
                  <b>Event Info</b>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={allSites ? 5 : 4} align="center">
                  <Typography variant="body1" color="textSecondary" sx={{ py: 2 }}>
                    No usage found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              usages.map((usage, index) => (
                <TableRow key={index}>
                  {allSites && <TableCell>{usage._site?.name || ''}</TableCell>}
                  <TableCell>{getUserName(usage)}</TableCell>
                  <TableCell>{usage.what}</TableCell>
                  <TableCell>
                    {getTZDateTimeAsString(usage.when, user?._site?.timezone || 'system')}
                  </TableCell>
                  <TableCell>{getEventInfo(usage)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ErrorDialog open={openErrorDialog} onErrorDialogClose={handleClose} errorMessage={errorMessage} />
    </div>
  );
};

Usage.propTypes = {
  allSites: PropTypes.bool,
};

export default Usage;